import type { CaptionPosition } from "../types/captionPosition";
import type { CaptionStyle } from "../types/captionStyle";

// Where the recorder draws the caption band and the nose target, in % of the video box (x of its width,
// y of its height). The target is portrait-only, as the captioned recorder is; the band table keeps every
// orientation because it mirrors the API.
export type CaptionOrientation = "portrait" | "landscape" | "square";

export interface Band {
    top: number;
    height: number;
}

export type NoseLabel = { top: number } | null;

export interface NoseTarget {
    // Where the nose goes (the ring's centre).
    nose: number;
    // The face zone the corner brackets mark: eyes to chin.
    zoneTop: number;
    zoneBottom: number;
    label: NoseLabel;
    // The caption band the target was placed around.
    band: Band;
}

// CAPTION_TOP and TEMPLATE_TOP mirror marabot-api src/third-party-api/zapcap/zapcap.config.ts (keep in sync).
// OFFSET was measured in the 2026-09-29 render check; it is not a constant there.
const CAPTION_TOP: Record<CaptionOrientation, Record<Exclude<CaptionPosition, "bottom">, number>> = {
    portrait: { top: 5, middle: 35 },
    landscape: { top: 0, middle: 20 },
    square: { top: 0, middle: 24 },
};

const TEMPLATE_TOP: Record<CaptionStyle, Record<CaptionOrientation, number>> = {
    clean: { portrait: 60, landscape: 40, square: 60 }, // celine
    boldPop: { portrait: 60, landscape: 40, square: 60 }, // ella
    glow: { portrait: 65, landscape: 40, square: 65 }, // devin
    classic: { portrait: 65, landscape: 40, square: 65 }, // dan
    chat: { portrait: 60, landscape: 40, square: 60 }, // jordan
    soft: { portrait: 75, landscape: 60, square: 75 }, // luke
};

const OFFSET: Record<CaptionOrientation, number> = { portrait: 15, landscape: 30, square: 26 };

// The vendor centres the text OFFSET below `top`. The band is the whole zone captions can land in:
// Bottom runs from 8 above the centre (room for the emoji row) to the bottom edge, Top from the top
// edge to 8 below the centre, Middle 10 either side of the centre.
export function captionBand(position: CaptionPosition, orientation: CaptionOrientation, style: CaptionStyle): Band {
    const top = position === "bottom" ? TEMPLATE_TOP[style][orientation] : CAPTION_TOP[orientation][position];
    const centre = top + OFFSET[orientation];
    if (position === "bottom") return { top: centre - 8, height: 100 - (centre - 8) };
    if (position === "top") return { top: 0, height: centre + 8 };
    return { top: centre - 10, height: 20 };
}

// Nose centre by caption position: clear of the band, and below the script when it shows.
// The nose sits where the earlier head-and-shoulders outline put the head's centre.
const NOSE: Record<"free" | "prompter", Record<CaptionPosition, number>> = {
    free: { bottom: 33, middle: 22, top: 50 },
    prompter: { bottom: 44, middle: 74, top: 56 },
};
const PROMPTER_ZONE: readonly [number, number] = [0, 33.4];
export const PROMPTER_BOTTOM = PROMPTER_ZONE[1];

// A band whose middle, where its label sits, is under the script (Top captions with the prompter) cannot carry the label.
export const bandUnderPrompter = (band: Band, prompterOnTop: boolean): boolean =>
    prompterOnTop && band.top + band.height / 2 <= PROMPTER_BOTTOM;

// The brackets run from just above the eyes to below the chin.
const ZONE_ABOVE = 3.5;
const ZONE_BELOW = 17;
// The label pill sits under the ring, inside the zone; PILL is its height.
const LABEL_GAP = 6.5;
const PILL = 4;

// "Center your nose here" goes under the ring, else above it, each clear of the frame edge and
// 1 % clear of every zone; else nowhere.
export function placeNoseLabel(nose: number, zones: ReadonlyArray<readonly [number, number]>): NoseLabel {
    const clear = (y: number): boolean =>
        y >= 1 && y + PILL <= 99 && zones.every(([a, b]) => y + PILL <= a - 1 || y >= b + 1);
    const below = nose + LABEL_GAP;
    const above = nose - LABEL_GAP - PILL;
    if (clear(below)) return { top: below };
    if (clear(above)) return { top: above };
    return null;
}

export function noseTarget(position: CaptionPosition, prompterOnTop: boolean, style: CaptionStyle): NoseTarget {
    const nose = NOSE[prompterOnTop ? "prompter" : "free"][position];
    const band = captionBand(position, "portrait", style);
    const zones: Array<readonly [number, number]> = [[band.top, band.top + band.height]];
    if (prompterOnTop) zones.push(PROMPTER_ZONE);
    return {
        nose,
        zoneTop: nose - ZONE_ABOVE,
        zoneBottom: nose + ZONE_BELOW,
        label: placeNoseLabel(nose, zones),
        band,
    };
}
